<template>
  <div>
    <v-card key="upload-document">
      <v-card-title> Upload new version</v-card-title>
      <v-card-text>
        <v-alert
          outlined
          type="info"
        >
          The file you upload will replace the current version of this document.
          You will not be charged for uploading or sending the new version of this document.
        </v-alert>
        <v-form
          ref="uploadFileForm"
          v-model="docDetailsValid"
        >
          <file-input
            label="File*"
            required
            :accept="uploadAcceptExtensions"
            :rules="[
              validateRequiredFile,
              validateFileMimeType
            ]"
            @change="(files) => filesChange(files)"
          />

          <v-text-field
            ref="docName"
            v-model="docName"
            :rules="[validateRequired]"
            label="Document Name*"
            required
            @blur="docNameDirty = true"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="uploading"
          small
          text
          @click="cancelHandler"
        >
          Cancel
        </v-btn>
        <v-btn
          :loading="uploading"
          :disabled="!canUpload"
          small
          color="primary"
          @click="uploadHandler()"
        >
          Upload
        </v-btn>
        <prepare-document-dialog
          :show-dialog="showPrepareDocumentDialog"
          :doc-data="documentData"
          @done="donePreparing"
        />
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { uploadAcceptExtensions } from '@/utils/mimeTypes'
import { validateRequired, validateRequiredFile, validateFileMimeType } from '@/utils/validationRules'

const FileInput = () => import('@/components/utils/OlDragAndDropFileInput')
const PrepareDocumentDialog = () => import('@/components/PrepareDocumentDialog')

export default {
  name: 'DashboardUploadNewVersion',
  components: {
    PrepareDocumentDialog,
    FileInput
  },
  props: {
    document: { type: Object, required: true }
  },
  data: function () {
    return {
      uploadAcceptExtensions,
      docName: null,
      docDetailsValid: false,
      uploading: false,
      documentData: null,
      files: null,
      showPrepareDocumentDialog: false
    }
  },
  computed: {
    canUpload() {
      return this.docDetailsValid
    }
  },
  watch: {
    document: {
      immediate: true,
      handler(newVal) {
        this.docName = newVal.name
      }
    }
  },
  methods: {
    validateRequired,
    validateRequiredFile,
    validateFileMimeType,
    cancelHandler() {
      this.$emit('close')
    },
    uploadHandler() {
      this.uploadToStorage()
    },
    donePreparing() {
      this.showPrepareDocumentDialog = false
      this.$emit('close')
    },
    prepareDocHandler() {
      this.showPrepareDocumentDialog = true
    },
    createFormData(files) {
      const forms = []
      for (const file of files) {
        const form = new FormData()
        form.append('data', file, file.name)
        forms.push(form)
      }
      return forms
    },
    async uploadToStorage() {
      this.uploading = true
      const file = this.files[0]
      await this.$handlingError(
        `Error uploading document "${this.docName}"`,
        async () => {
          await this.$api.uploadNewDocumentVersion(this.document.id, file, { name: this.docName })

          this.documentData = {
            documentId: this.document.id,
            name: this.docName
          }

          this.prepareDocHandler()
          this.$showSuccessbar('New version uploaded.')
          this.cancelHandler()
        },
        {
          final: () => {
            this.uploading = false
          }
        }
      )
    },
    filesChange(files) {
      // if docName is blank, give it the same name the file, minus the extension
      if (!this.docName && files && files.length) {
        this.docName = this.stripExtension(files[0].name)
      }

      this.files = files
    },
    stripExtension(filename) {
      if (!filename) {
        return ''
      }
      return filename.replace(/\.[^/.]+$/, '')
    }
  }
}
</script>

<style lang="scss" scoped>
.v-btn.v-size--default.back-button {
  padding-left: 0;
}
</style>
